import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { ComponentProps, forwardRef } from 'react'

import cn from '@/utilities/cn'

type TextAreaProps = ComponentProps<'textarea'> & {
  error?: string
  name: string
  disabled?: boolean
  description?: string
  descriptionLocation?: 'before' | 'after'
  label?: string
  placeholderIcon?: any
  boldHeaders?: boolean
  required?: boolean
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      error,
      className,
      label,
      name,
      disabled,
      description,
      boldHeaders = false,
      placeholderIcon,
      descriptionLocation = 'before',
      required,
      ...rest
    }: TextAreaProps,
    ref
  ) => {
    return (
      <div>
        {label && (
          <div className="row">
            <label
              htmlFor={name}
              className={cn('block text-sm font-medium leading-6 text-gray-900', { 'font-bold': boldHeaders })}
            >
              {label}
            </label>
            {required && <span className="ml-1 text-red-500">*</span>}
          </div>
        )}
        {description && descriptionLocation === 'before' && (
          <span className="text-sm text-zinc-500">{description}</span>
        )}
        <div className="relative mt-2 rounded-md shadow-sm">
          {placeholderIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-start pl-3 pt-2">
              {placeholderIcon}
            </div>
          )}
          <textarea
            rows={3}
            ref={ref}
            disabled={disabled}
            className={cn(
              error ? 'focus:ring-red-600' : 'focus:ring-blue-600',
              error ? 'ring-red-300' : 'ring-gray-300',
              disabled ? 'bg-gray-50' : '',
              placeholderIcon ? 'pl-10' : '',
              'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-1 focus:ring-inset sm:text-sm sm:leading-6',
              className
            )}
            {...rest}
          />
          {error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-start pt-2 pr-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
          )}
        </div>
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        {description && descriptionLocation === 'after' && <span className="text-sm text-zinc-500">{description}</span>}
      </div>
    )
  }
)

TextArea.displayName = 'TextArea'

export default TextArea
